import React from 'react';
import CTA from './CTA';
import { IMAGES } from '../constants/images';
import useIsMobile from '../utils/useIsMobile';
import { FiFileText } from 'react-icons/fi';

const DotGrid = () => (
  <div aria-hidden style={{
    position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  }} />
);

/**
 * Reusable legal document layout (Terms, Cookie Policy, etc.).
 * Sections are rendered fully visible (not collapsed) so the content is
 * readable by users, search engines, and ad-policy review tools.
 *
 * Props:
 *  - eyebrow: small label above the title
 *  - title / titleAccent: heading (accent shown in blue)
 *  - intro: lead paragraph under the title
 *  - updated: "last updated" string
 *  - sections: [{ heading, paragraphs?: string[], items?: string[] }]
 */
const LegalDoc = ({ eyebrow, title, titleAccent, intro, updated, sections = [] }) => {
  const isMobile = useIsMobile(768);

  return (
    <main style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: '#f8f9fc' }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: isMobile ? 'clamp(280px, 60vh, 360px)' : 'clamp(360px, 50vh, 440px)', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <img src={IMAGES.heroPrivacy} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.src = 'https://picsum.photos/1600/900?blur=2'; }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(10,20,60,0.88) 0%, rgba(10,20,60,0.62) 55%, rgba(10,20,60,0.28) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.5) 0%, transparent 40%)' }} />
        <DotGrid />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: isMobile ? 'clamp(48px, 10vw, 64px) var(--cc-px) clamp(32px, 6vw, 44px)' : 'clamp(72px, 8vw, 100px) var(--cc-px) 70px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999,
            padding: '6px 18px', marginBottom: 22,
          }}>
            <FiFileText style={{ color: '#60a5fa', width: 15, height: 15 }} />
            <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{eyebrow}</span>
          </div>

          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 6rem)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            {title} {titleAccent && <span style={{ color: '#60a5fa' }}>{titleAccent}</span>}
          </h1>
          {intro && (
            <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '0.97rem' : '1.12rem', maxWidth: 660, margin: '0 auto 22px', lineHeight: 1.8 }}>
              {intro}
            </p>
          )}
          {updated && (
            <div style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999,
              padding: '6px 18px', color: 'rgba(255,255,255,0.9)', fontSize: '0.83rem', fontWeight: 700,
            }}>
              {updated}
            </div>
          )}
        </div>
      </section>

      {/* ── CONTENT ──────────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(28px, 6vw, 40px) var(--cc-px) clamp(40px, 8vw, 64px)' : 'clamp(48px, 5vw, 72px) var(--cc-px) 80px', maxWidth: 'var(--content-narrow)', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 20 }}>
          {sections.map((section, i) => (
            <article key={i} style={{
              background: '#fff', borderRadius: 16,
              border: '1.5px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              padding: isMobile ? '20px var(--cc-px)' : '28px 32px',
            }}>
              <h2 style={{ fontWeight: 800, fontSize: isMobile ? '1.05rem' : '1.2rem', color: '#0f172a', margin: '0 0 14px', letterSpacing: '-0.01em' }}>
                {section.heading}
              </h2>
              {section.paragraphs && section.paragraphs.map((p, j) => (
                <p key={j} style={{ color: '#334155', lineHeight: 1.85, margin: j === section.paragraphs.length - 1 && !section.items ? 0 : '0 0 14px', fontSize: isMobile ? '0.92rem' : '1rem' }}>
                  {p}
                </p>
              ))}
              {section.items && (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {section.items.map((item, j) => (
                    <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563eb', flexShrink: 0, marginTop: 8 }} />
                      <span style={{ color: '#334155', fontSize: isMobile ? '0.9rem' : '1rem', lineHeight: 1.75 }}>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default LegalDoc;
